import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableRow, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from "react-i18next";
import getWorkcellColor from "../utils/getWorkcellColor";
import { fetchMonthlyTurnover } from "../api";
import { useCustomerCurrency } from "../context/CustomerSettingsContext";

const MonthlyTurnoverTable = ({ date }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const { formatCurrency } = useCustomerCurrency();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!date) {
      setData([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    fetchMonthlyTurnover(date)
      .then((res) => {
        setData(res || []);
      })
      .catch(() => {
        setData([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [date]);
  // Group by workcell
  const workcellMap = {};
  data.forEach(row => {
    if (!workcellMap[row.workcell_id]) {
      workcellMap[row.workcell_id] = {
        workcell_name: row.workcell_name,
        workcell_id: row.workcell_id,
        op: 0,
        hr: 0,
        total: 0
      };
    }
    workcellMap[row.workcell_id].op += row.operation_turnover || 0;
    workcellMap[row.workcell_id].hr += row.hourly_turnover || 0;
    workcellMap[row.workcell_id].total += row.total_turnover || 0;
  });
  const rows = Object.values(workcellMap);
  // Assign color order before sorting
  const allWorkcellIds = rows.map(r => r.workcell_id).sort((a, b) => a - b);
  // Sort rows by total turnover descending for display
  const sortedRows = [...rows].sort((a, b) => b.total - a.total);
  const totalOp = rows.reduce((sum, r) => sum + r.op, 0);
  const totalHr = rows.reduce((sum, r) => sum + r.hr, 0);
  const totalTotal = rows.reduce((sum, r) => sum + r.total, 0);
  if (isLoading) return <div>{t("common.loading")}</div>;
  if (rows.length === 0) return <div>{t("common.noData")}</div>;
  return (
    <Table stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell>{t("monthly.overview.turnoverTable.workcell")}</TableCell>
          <TableCell align="right">{t("monthly.overview.turnoverTable.operationTurnover")}</TableCell>
          <TableCell align="right">{t("monthly.overview.turnoverTable.hourlyTurnover")}</TableCell>
          <TableCell align="right">{t("monthly.overview.turnoverTable.totalTurnover")}</TableCell>
          <TableCell sx={{ width: '10rem' }} />
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedRows.map(row => (
          <TableRow key={row.workcell_id}>
            <TableCell component="th" scope="row" sx={{ color: getWorkcellColor(row.workcell_id, theme.palette.chart.colors, allWorkcellIds), fontWeight: 'bold' }}>{row.workcell_name}</TableCell>
            <TableCell align="right">{formatCurrency(row.op)}</TableCell>
            <TableCell align="right">{formatCurrency(row.hr)}</TableCell>
            <TableCell align="right">{formatCurrency(row.total)}</TableCell>
            <TableCell align="right">
              <Button
                variant="contained"
                size="small"
                onClick={() => navigate(`/chart/workcell/${row.workcell_id}/monthly/${date}`)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {t("common.viewDetails")}
              </Button>
            </TableCell>
          </TableRow>
        ))}
        <TableRow sx={{ fontWeight: 'bold', backgroundColor: theme.palette.action.hover }}>
          <TableCell component="th" scope="row">{t("common.total")}</TableCell>
          <TableCell align="right">{formatCurrency(totalOp)}</TableCell>
          <TableCell align="right">{formatCurrency(totalHr)}</TableCell>
          <TableCell align="right">{formatCurrency(totalTotal)}</TableCell>
          <TableCell sx={{ width: '10rem' }} />
        </TableRow>
      </TableBody>
    </Table>
  );
};

export default MonthlyTurnoverTable;
